import {
  Alert,
  Button,
  Col,
  ConsoleCard,
  Form,
  Input,
  Page,
  PageHeading,
  Row,
  Select,
} from 'admin-web-ui'
import { Store } from 'rc-field-form/lib/interface'
import React, { useState } from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { useProjectContext } from '../../containers/Project'
import {
  getFullUserByEmail,
  getFullUserBySub,
  getFullUserByUsername,
} from './helpers'
import {
  EmailSection,
  EntitlementSection,
  UserInfoSection,
  VirtualCardsSection,
} from './sections'

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  gap: 20px;
  p,
  a {
    margin: 0;
    font-size: 14px;
    line-height: 19px;
  }
`

const SearchSelect = styled(Form.Item)`
  width: 200px;
`

const SearchInput = styled(Form.Item)`
  flex-grow: 1;
  margin-left: 24px;
`

const SubmitButton = styled(Button)`
  min-width: 112px;
`

const StyledAlert = styled(Alert)`
  margin-top: 24px;
`

const StyledInput = styled(Input)`
  ::placeholder {
    text-transform: capitalize;
  }
`

interface FormData {
  searchParam: string
  searchTerm: string
}

export const UserLookup: React.FC = () => {
  const { projectClient, services } = useProjectContext()
  const [searchParam, setSearchParam] = useState<string | undefined>()
  const [isErrorCleared, setIsErrorCleared] = useState<boolean>(true)

  const [submitState, submit] = useAsyncFn(async (store?: Store) => {
    setIsErrorCleared(false)
    if (!store) {
      return undefined
    }

    const { searchParam, searchTerm: rawSearchTerm } = store as FormData
    const searchTerm = rawSearchTerm.trim()
    switch (searchParam) {
      case 'platform username':
        return getFullUserByUsername(projectClient, searchTerm, services)
      case 'platform subject':
        return getFullUserBySub(projectClient, searchTerm, services)
      case 'sudo email address':
        return getFullUserByEmail(projectClient, searchTerm, services)
      default:
        throw new Error('Not a valid search parameter.')
    }
  }, [])

  return (
    <Page>
      <PageHeading>User Lookup</PageHeading>
      <Row>
        <Col span={24}>
          <ConsoleCard>
            <Form onFinish={submit} layout="vertical">
              <SearchContainer>
                <SearchSelect
                  name="searchParam"
                  label="Search by"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a search option',
                    },
                  ]}
                  initialValue="platform username"
                >
                  <Select
                    placeholder="Select a search option"
                    onChange={(option: string) => setSearchParam(option)}
                  >
                    <Select.Option key={'1'} value={'platform username'}>
                      Platform Username
                    </Select.Option>
                    <Select.Option key={'2'} value={'platform subject'}>
                      Platform Subject
                    </Select.Option>
                    <Select.Option key={'3'} value={'sudo email address'}>
                      Sudo Email Address
                    </Select.Option>
                  </Select>
                </SearchSelect>
                <SearchInput
                  name="searchTerm"
                  label="Search term"
                  rules={[
                    {
                      required: true,
                      message: `Please input a search term`,
                    },
                  ]}
                >
                  <StyledInput placeholder={searchParam} />
                </SearchInput>
              </SearchContainer>
              <SubmitButton
                type="primary"
                shape="round"
                loading={submitState.loading}
                htmlType="submit"
              >
                Submit
              </SubmitButton>
            </Form>
            {submitState.error && !submitState.loading && !isErrorCleared ? (
              <StyledAlert
                message={submitState.error.message}
                type="error"
                banner
                closable
                onClose={() => setIsErrorCleared(true)}
              />
            ) : (
              submitState.value && (
                <SearchResults>
                  <UserInfoSection {...submitState.value} />
                  <EntitlementSection
                    entitlements={submitState.value.entitlements}
                  />
                  <EmailSection userEmails={submitState.value.userEmails} />
                  <VirtualCardsSection
                    username={submitState.value.username}
                    virtualCards={submitState.value.virtualCards}
                    fundingSources={submitState.value.fundingSources}
                  />
                </SearchResults>
              )
            )}
          </ConsoleCard>
        </Col>
      </Row>
    </Page>
  )
}
